import { create } from "zustand";
import type { PresenceDto } from "../contract/chat.events";
import { TYPING_DISPLAY_MS } from "../lib/constants";
import type { ConnectionStatus } from "../socket/types";

interface PresenceState {
  isOnline: boolean;
  lastSeenAt: string | null;
}

/**
 * Short-lived realtime state that is not server data: connection, network,
 * app activity, typing and presence. Components subscribe by id
 * (useIsTyping(conversationId), usePresence(userId)), so a typing or presence
 * event re-renders only the row/header it concerns.
 */
interface EphemeralState {
  connection: ConnectionStatus;
  online: boolean;
  appActive: boolean;
  /** conversationId → typing (true while a fresh `typing on` is < 8 s old). */
  typing: Record<string, true>;
  presence: Record<number, PresenceState>;
  setConnection: (status: ConnectionStatus) => void;
  setOnline: (online: boolean) => void;
  setAppActive: (active: boolean) => void;
  setTyping: (conversationId: string, on: boolean) => void;
  setPresence: (p: PresenceDto) => void;
  clearPresence: () => void;
  reset: () => void;
}

const typingTimers = new Map<string, ReturnType<typeof setTimeout>>();

const initial = {
  connection: "idle" as ConnectionStatus,
  online: typeof navigator === "undefined" ? true : navigator.onLine,
  appActive: typeof document === "undefined" ? true : document.visibilityState === "visible",
  typing: {} as Record<string, true>,
  presence: {} as Record<number, PresenceState>,
};

export const useChatEphemeral = create<EphemeralState>()((set) => ({
  ...initial,
  setConnection: (connection) => set({ connection }),
  setOnline: (online) => set({ online }),
  setAppActive: (appActive) => set({ appActive }),
  setTyping: (conversationId, on) => {
    const timer = typingTimers.get(conversationId);
    if (timer) clearTimeout(timer);
    typingTimers.delete(conversationId);
    if (on) {
      // The receiver hides the indicator itself after 8 s without a new
      // `typing on` (CONTRACT §6).
      typingTimers.set(
        conversationId,
        setTimeout(() => useChatEphemeral.getState().setTyping(conversationId, false), TYPING_DISPLAY_MS),
      );
    }
    set((s) => {
      if (on === Boolean(s.typing[conversationId])) return s;
      const typing = { ...s.typing };
      if (on) typing[conversationId] = true;
      else delete typing[conversationId];
      return { typing };
    });
  },
  setPresence: (p) =>
    set((s) => ({ presence: { ...s.presence, [p.userId]: { isOnline: p.isOnline, lastSeenAt: p.lastSeenAt } } })),
  clearPresence: () => set({ presence: {} }),
  reset: () => {
    typingTimers.forEach(clearTimeout);
    typingTimers.clear();
    set({ ...initial, online: typeof navigator === "undefined" ? true : navigator.onLine });
  },
}));

export const useIsTyping = (conversationId: string | undefined): boolean =>
  useChatEphemeral((s) => (conversationId ? Boolean(s.typing[conversationId]) : false));

/** Live presence overrides what the DTO said when it was loaded. */
export function usePresence(
  userId: number | undefined,
  fallback: { isOnline: boolean; lastSeenAt: string | null } | undefined,
): { isOnline: boolean; lastSeenAt: string | null } {
  const isOnline = useChatEphemeral((s) => (userId !== undefined ? s.presence[userId]?.isOnline : undefined));
  const lastSeenAt = useChatEphemeral((s) => (userId !== undefined ? s.presence[userId]?.lastSeenAt : undefined));
  return {
    isOnline: isOnline ?? fallback?.isOnline ?? false,
    lastSeenAt: lastSeenAt !== undefined ? lastSeenAt : (fallback?.lastSeenAt ?? null),
  };
}
